import { useState } from 'react'

import { IAlcatrazTicketRow } from '@/common/models/settings'
import { Button } from '@/components/ui'
import { IColumn, Table } from '../../../core/components/Table'
import { formatDateTime } from '../../../core/constants/times'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'

export interface AlcatrazTicketsTableProps {
  rows: IAlcatrazTicketRow[]
  sortBy?: string
  sortDirection?: 'DESC' | 'ASC'
  setSortDirection?: (s?: 'DESC' | 'ASC') => void
  setSortBy?: (sort?: string) => void
  handleSearchParams?: (col: IColumn, query: string | null) => void
  loading?: boolean
  noDataMessage?: string | JSX.Element
  hasEditPermission?: boolean
  maxHeight?: string
  onRegister: (row: IAlcatrazTicketRow) => void
  onDelete: (row: IAlcatrazTicketRow) => void
}

export const AlcatrazTicketsTable = (
  props: AlcatrazTicketsTableProps,
): JSX.Element => {
  const { sortBy, setSortBy, sortDirection, setSortDirection } = props
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false)
  const [selectedRow, setSelectedRow] = useState<IAlcatrazTicketRow | null>(
    null,
  )

  const handleSortBy = (sort: string) => {
    if (!setSortBy || !setSortDirection) return
    if (sort === sortBy) {
      if (sortDirection === 'DESC') {
        setSortDirection('ASC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('DESC')
      setSortBy(sort)
    }
  }

  const columns: IColumn[] = [
    {
      title: 'Name',
      searchable: true,
      accessor: 'name',
      render: (row: IAlcatrazTicketRow) => <span>{row.name}</span>,
    },
    {
      title: 'Email',
      searchable: true,
      accessor: 'email',
      render: (row: IAlcatrazTicketRow) => <span>{row.email}</span>,
    },
    {
      title: 'Phone',
      accessor: 'phone',
      render: (row: IAlcatrazTicketRow) => <span>{row.phone}</span>,
    },
    {
      title: 'Ticket Code',
      searchable: true,
      accessor: 'ticketCode',
      render: (row: IAlcatrazTicketRow) => (
        <span className="whitespace-nowrap">{row.ticketCode}</span>
      ),
    },
    {
      title: 'Enrollment Code',
      accessor: 'alcatrazEnrollmentCode',
      render: (row: IAlcatrazTicketRow) =>
        row.alcatrazEnrollmentCode ? (
          <span className="whitespace-nowrap">
            {row.alcatrazEnrollmentCode}
          </span>
        ) : (
          <span className="text-monochrome-2">—</span>
        ),
    },
    {
      title: 'Festival Day',
      accessor: 'festivalDayStartAt',
      sortable: true,
      render: (row: IAlcatrazTicketRow) => (
        <span className="whitespace-nowrap">
          {formatDateTime(row.festivalDayStartAt)}
        </span>
      ),
    },
    {
      title: 'Actions',
      accessor: '',
      render: (row: IAlcatrazTicketRow) => (
        <div className="flex items-center gap-2 whitespace-nowrap">
          {!row.enrollmentCompleted && (
            <Button
              variant="primary-bordered"
              size="sm"
              disabled={!props.hasEditPermission}
              onClick={() => props.onRegister(row)}
            >
              Register
            </Button>
          )}
          <Button
            variant="destructive"
            size="sm"
            disabled={!props.hasEditPermission}
            onClick={() => {
              setSelectedRow(row)
              setShowDeleteConfirmationModal(true)
            }}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ]

  return (
    <>
      <Table
        columns={columns}
        data={props.rows}
        handleSortBy={handleSortBy}
        handleSearch={props.handleSearchParams}
        loading={props.loading}
        noDataMessage={props.noDataMessage}
        className="[&_th]:!whitespace-nowrap"
        maxHeight={props.maxHeight}
        getRowId={(row: IAlcatrazTicketRow) => row.participationId}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setShowDeleteConfirmationModal}
        deleteTitle="Ticket"
        size="md"
        deleteFunc={() => {
          if (selectedRow) {
            props.onDelete(selectedRow)
          }
        }}
        onClose={() => setSelectedRow(null)}
      />
    </>
  )
}
